import { z } from 'zod/v3';
import { LanguageCodeSchema, ComplianceFrameworkSchema, WorkspaceTypeSchema } from "../generated/enums";

// Enhanced description guidance
const descriptionSchema = z
  .string()
  // .min(10, "Please provide a detailed description of at least 10 characters")
  .max(5000, "Description must be less than 5000 characters")
  .nullish()
  .describe(
    "Describe what this workspace is about, including the product, audience, and purpose of the documentation."
  );

// Workspace DTOs
export const WorkspaceDTOSchema = z
  .object({
    id: z.string(),
    slug: z.string(),
    title: z.string(),
    description: z.string().nullish(),
    organizationId: z.string().nullable(),
    ownerId: z.string(),
    createdAt: z.date(),
    updatedAt: z.date(),
    tone: z.string().nullish(),
    documentKind: z.string().nullish(),
    targetAudience: z.string().nullish(),
    otherInstructions: z.string().nullish(),
    type: WorkspaceTypeSchema,
    evidenceFilenameTemplate: z.string().nullish(),
  })
  .strict();

export const WorkspaceListDTOSchema = z
  .object({
    id: z.string(),
    title: z.string(),
    description: z.string().optional(),
    createdAt: z.date(),
    updatedAt: z.date(),
  })
  .strict();

// Create workspace
export const CreateWorkspaceDTOSchema = z
  .object({
    title: z.string().min(2, "Title must be at least 2 characters"),
    description: descriptionSchema.optional(),
    defaultLanguage: LanguageCodeSchema.optional(),
    slug: z.string().min(2, "Slug must be at least 2 characters").optional(),
    applicationHomeUrl: z.string().optional(),
    customDomain: z.string().optional().nullable(),
    publicLandingUrl: z.string().optional(),
    tone: z.string().optional(),
    documentKind: z.string().optional(),
    targetAudience: z.string().optional(),
    otherInstructions: z.string().optional(),
    mountType: z.enum(['NOTION', 'GITHUB', 'GOOGLE_DOCS', 'CONFLUENCE']).optional(),
    // Workspace type and features
    type: WorkspaceTypeSchema.optional(),
    enabledFrameworks: z.array(ComplianceFrameworkSchema).optional(),
    enabledFeatures: z.array(z.string()).optional(),
    // Task code prefix (e.g., "DEE" for DEE-1, DEE-2)
    taskCodePrefix: z.string().max(10).nullish(),
    evidenceFilenameTemplate: z.string().max(240).nullish(),
  })
  .strict();

export const CreateDefaultWorkspaceDTOSchema = CreateWorkspaceDTOSchema.extend({
  ownerId: z.string(),
  organizationId: z.string(),
}).strict();

// Update workspace
export const UpdateWorkspaceDTOSchema = z
  .object({
    id: z.string(),
    title: z.string().min(2, "Title must be at least 2 characters").optional(),
    description: descriptionSchema.optional(),
    slug: z.string().min(2, "Slug must be at least 2 characters").optional(),
    defaultLanguage: LanguageCodeSchema.optional(),
    publicLandingUrl: z.string().optional(),
    applicationHomeUrl: z.string().optional(),
    customDomain: z.string().optional().nullable(),
    // Writing
    tone: z.string().nullish(),
    documentKind: z.string().nullish(),
    targetAudience: z.string().nullish(),
    otherInstructions: z.string().nullish(),
    // Site theme
    logo: z.string().nullish(),
    logoDark: z.string().nullish(),
    favicon: z.string().nullish(),
    primaryColor: z.string().nullish(),
    darkPrimaryColor: z.string().nullish(),
    darkBackgroundColor: z.string().nullish(),
    darkTextColor: z.string().nullish(),
    backgroundType: z.string().nullish(),
    backgroundDirection: z.string().nullish(),
    backgroundStartColor: z.string().nullish(),
    darkBackgroundStartColor: z.string().nullish(),
    backgroundEndColor: z.string().nullish(),
    darkBackgroundEndColor: z.string().nullish(),
    titleFont: z.string().nullish(),
    contentFont: z.string().nullish(),
    siteFont: z.string().nullish(),
    themeMode: z.string().nullish(),
    defaultEditorMode: z.enum(['video', 'docs']).optional(), // From primaryFocus selection
    adaptiveMode: z.boolean().optional(), // True if user selected 'adaptive'
    // Compliance settings (RFC-081)
    disableAutoSegmentation: z.boolean().optional(), // Disable auto-segmentation for compliance workflows
    enabledFrameworks: z.array(ComplianceFrameworkSchema).optional(), // Enabled compliance frameworks
    taskCodePrefix: z.string().max(10).nullish(), // Custom prefix for task codes (e.g., "DEE" for DEE-1, DEE-2)
    evidenceFilenameTemplate: z.string().max(240).nullish(),
    // AC-3: Segregation of duties - prevent creator from approving their own evidence
    enforceCreatorApproverSeparation: z.boolean().optional(),
    // RFC-202: when false, only a test's designated Approver (plus ADMIN/OWNER
    // break-glass) may approve its evidence; when true (default) any reviewer may.
    allowApproverOverride: z.boolean().optional(),
    // Site configuration
    headerNavItems: z
      .array(
        z.object({
          id: z.string(),
          name: z.string(),
          url: z.string(),
        })
      )
      .nullish(),
    headerBgColor: z.string().nullish(),
    textColor: z.string().nullish(),
    headerTitle: z.string().nullish(),
    headerDescription: z.string().nullish(),
    footerNavItems: z
      .array(
        z.object({
          id: z.string(),
          name: z.string(),
          url: z.string(),
        })
      )
      .nullish(),
    footerBgColor: z.string().nullish(),
    footerTextColor: z.string().nullish(),
    footerCopyright: z.string().nullish(),
    footerSocialLinks: z
      .object({
        facebook: z.string().nullish(),
        twitter: z.string().nullish(),
        linkedin: z.string().nullish(),
      })
      .nullish(),
    // Custom HTML sections
    customHeaderHtml: z.string().nullish(),
    customFooterHtml: z.string().nullish(),
    showSearch: z.boolean().nullish(),
    headerTextColor: z.string().nullish(),
    backgroundColor: z.string().nullish(),
  })
  .strict();

// Get workspaces query
export const GetWorkspacesQueryDTOSchema = z
  .object({
    organizationId: z.string(),
    limit: z.number().min(1).max(100).default(50),
    cursor: z.string().optional(),
  })
  .strict();

// Get workspace by ID
export const GetWorkspaceByIdDTOSchema = z
  .object({
    id: z.string(),
    organizationId: z.string(),
  })
  .strict();

// Delete workspace
export const DeleteWorkspaceDTOSchema = z
  .object({
    id: z.string(),
    organizationId: z.string(),
  })
  .strict();

export type WorkspaceDTO = z.infer<typeof WorkspaceDTOSchema>;
export type WorkspaceListDTO = z.infer<typeof WorkspaceListDTOSchema>;
export type CreateWorkspaceDTO = z.infer<typeof CreateWorkspaceDTOSchema>;
export type UpdateWorkspaceDTO = z.infer<typeof UpdateWorkspaceDTOSchema>;
export type GetWorkspacesQueryDTO = z.infer<typeof GetWorkspacesQueryDTOSchema>;
export type GetWorkspaceByIdDTO = z.infer<typeof GetWorkspaceByIdDTOSchema>;
export type DeleteWorkspaceDTO = z.infer<typeof DeleteWorkspaceDTOSchema>;
export type CreateDefaultWorkspaceDTO = z.infer<typeof CreateDefaultWorkspaceDTOSchema>;
